import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, money } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tag, Plus } from "@phosphor-icons/react";

export default function Pricing() {
  const { user } = useAuth();
  const [rates, setRates] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState("");
  const [target, setTarget] = useState("__default__");
  const [saving, setSaving] = useState(false);
  const [myRate, setMyRate] = useState(null);

  const isAdmin = user?.role === "admin";
  const rateType = isAdmin ? "platform" : "reseller";
  const targets = tenants.filter((t) => t.type === (isAdmin ? "reseller" : "customer"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (user?.role === "customer") {
        const my = await api.get("/billing/call-limit");
        setRates([]);
        setMyRate(my.data);
      } else {
        const [r, t] = await Promise.all([api.get("/pricing"), api.get("/tenants")]);
        setRates(r.data);
        setTenants(t.data.filter((x) => x.type !== "platform"));
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const minor = Math.round(parseFloat(price) * 100);
    if (!minor || minor <= 0) { toast.error("Enter a valid price"); return; }
    setSaving(true);
    try {
      const body = { price_minor: minor, currency: "USD" };
      if (target !== "__default__") body.target_tenant_id = target;
      await api.post(`/pricing/${rateType}`, body);
      toast.success("New rate version published");
      setOpen(false); setPrice(""); setTarget("__default__");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const tName = (id) => {
    if (!id) return "Default (all)";
    const t = tenants.find((x) => x.id === id);
    return t ? t.name : "Deleted tenant";
  };

  return (
    <div>
      {user?.role === "customer" ? (
        <>
          <PageHeader title="Pricing" subtitle="Your price per concurrent channel" />
          <div className="max-w-md rounded-xl border border-border bg-card card-shadow p-8" data-testid="my-rate-card">
            <p className="text-xs uppercase tracking-[0.18em] font-medium text-muted-foreground">Your price / concurrent channel</p>
            <p className="text-4xl font-display font-semibold mt-3">{myRate?.call_limit_price_minor != null ? money(myRate.call_limit_price_minor) : "Not set"}</p>
            <p className="text-sm text-muted-foreground mt-2 capitalize">Pricing: {myRate?.call_limit_pricing_mode || "—"}</p>
          </div>
        </>
      ) : (
      <>
      <PageHeader
        title={isAdmin ? "Platform Pricing" : "Reseller Pricing"}
        subtitle="Effective-dated, versioned rates — never overwritten (append-only)"
      >
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-rate-btn" className="rounded-lg">
              <Plus className="w-4 h-4 mr-1.5" weight="bold" /> New Rate Version
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-rate-dialog">
            <DialogHeader><DialogTitle>Publish {rateType} rate</DialogTitle>
              <DialogDescription>Appends a new effective-dated rate version; prior versions are preserved.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Applies to</Label>
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger data-testid="rate-target-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Default (all {isAdmin ? "resellers" : "customers"})</SelectItem>
                    {targets.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Price per concurrency slot (USD)</Label>
                <Input data-testid="rate-price" type="number" step="0.01" min="0"
                  value={price} onChange={(e) => setPrice(e.target.value)} placeholder="10.00" />
                <p className="text-xs text-muted-foreground">Stored as integer minor-units. e.g. $10.00 → 1000.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid="submit-rate-btn" className="rounded-lg">
                {saving ? "Publishing…" : "Publish version"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rates.length === 0 ? (
        <EmptyState testid="pricing-empty" icon={Tag} title="No rates yet"
          description="Publish your first versioned rate. Existing holds keep their snapshot when rates change." />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="pricing-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Applies to</TableHead>
                <TableHead className="text-right">Price / slot</TableHead>
                <TableHead className="text-center">Version</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rates.map((r) => (
                <TableRow key={r.id} className="hover:bg-muted/40" data-testid={`rate-row-${r.id}`}>
                  <TableCell className="capitalize font-medium">{r.type}</TableCell>
                  <TableCell className="text-sm">{tName(r.target_tenant_id)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{money(r.price_minor)}</TableCell>
                  <TableCell className="text-center tabular-nums">v{r.version}</TableCell>
                  <TableCell>
                    <span className={r.effective_to ? "text-xs text-muted-foreground" : "text-xs text-[hsl(var(--success))] font-medium"}>
                      {r.effective_to ? "Superseded" : "Current"}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      </>
      )}
    </div>
  );
}

